import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// react-table components
import { useTable, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  page_no,
  hasNextPage,
  hasPrevPage,
  totalPages,
  totalDocs,
  isnextPage,
  prevPage,
  onPageChange,
  onEntriesPerPageChange,
  fitContainer,
}) {
  const configuredLimit =
    typeof entriesPerPage === "object" && Number(entriesPerPage.defaultValue) > 0
      ? Number(entriesPerPage.defaultValue)
      : 10;
  const configuredEntries =
    typeof entriesPerPage === "object" && Array.isArray(entriesPerPage.entries)
      ? entriesPerPage.entries
      : [10, 25, 50, 100];
  const entries = [...new Set([...configuredEntries, configuredLimit])]
    .filter((entry) => Number(entry) > 0)
    .map(Number)
    .sort((a, b) => a - b);

  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    setGlobalFilter,
    state: { globalFilter },
  } = tableInstance;

  // Search input value state
  const [search, setSearch] = useState(globalFilter);
  const [localPage, setLocalPage] = useState(1);
  const [localLimit, setLocalLimit] = useState(configuredLimit);

  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // Sorted value
  const setSortedValue = (column) => {
    if (isSorted && column.isSorted) {
      return column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      return "none";
    }
    return false;
  };

  const hasServerPagination =
    Number.isFinite(Number(page_no)) &&
    Number.isFinite(Number(totalPages)) &&
    Number.isFinite(Number(totalDocs)) &&
    typeof onPageChange === "function";
  const paginationEnabled = entriesPerPage !== false;
  const currentLimit = hasServerPagination
    ? configuredLimit
    : paginationEnabled
    ? localLimit
    : Math.max(rows.length, 1);
  const totalDocCount = hasServerPagination ? Math.max(0, Number(totalDocs)) : rows.length;
  const totalPageCount = hasServerPagination
    ? Math.max(1, Number(totalPages))
    : paginationEnabled
    ? Math.max(1, Math.ceil(totalDocCount / currentLimit))
    : 1;
  const currentPage = hasServerPagination
    ? Math.min(Math.max(1, Number(page_no)), totalPageCount)
    : Math.min(localPage, totalPageCount);
  const entriesStart = totalDocCount === 0 ? 0 : (currentPage - 1) * currentLimit + 1;
  const entriesEnd = Math.min(currentPage * currentLimit, totalDocCount);
  const visibleRows = hasServerPagination
    ? rows
    : rows.slice((currentPage - 1) * currentLimit, currentPage * currentLimit);

  useEffect(() => {
    if (!hasServerPagination && localPage > totalPageCount) {
      setLocalPage(totalPageCount);
    }
  }, [hasServerPagination, localPage, totalPageCount]);

  const handlePageChange = (event, nextPage) => {
    if (hasServerPagination) {
      onPageChange(nextPage);
      return;
    }
    setLocalPage(nextPage);
  };

  const handleEntriesPerPageChange = (event) => {
    const nextLimit = Number(event.target.value);
    if (!Number.isFinite(nextLimit) || nextLimit <= 0) {
      return;
    }

    if (hasServerPagination) {
      onEntriesPerPageChange(nextLimit);
      return;
    }

    setLocalLimit(nextLimit);
    setLocalPage(1);
    if (typeof onEntriesPerPageChange === "function") {
      onEntriesPerPageChange(nextLimit);
    }
  };

  return (
    <TableContainer
      sx={{
        boxShadow: "none",
        borderRadius: "18px",
        border: "1px solid rgba(126, 170, 220, 0.12)",
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(10,20,34,0.96) 0%, rgba(8,16,28,0.96) 100%)",
      }}
    >
      {/* Top Controls */}
      {canSearch ? (
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
          sx={{
            gap: 1.25,
            flexDirection: { xs: "column", md: "row" },
            background:
              "linear-gradient(135deg, rgba(17,35,56,0.92) 0%, rgba(12,24,39,0.96) 100%)",
            borderBottom: "1px solid rgba(126, 170, 220, 0.12)",
          }}
        >
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                  if (!hasServerPagination) {
                    setLocalPage(1);
                  }
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}

      {/* Table */}
      <MDBox
        sx={{
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Table
          {...getTableProps()}
          sx={{
            minWidth: fitContainer ? "100%" : "max-content",
            width: "100%",
            tableLayout: fitContainer ? "fixed" : "auto",
          }}
        >
          <MDBox component="thead">
            {headerGroups.map((headerGroup, key) => (
              <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, idx) => (
                  <DataTableHeadCell
                    key={idx}
                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                    width={column.width || "auto"}
                    align={column.align || "left"}
                    sorted={setSortedValue(column)}
                    compact={fitContainer}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </MDBox>
          <TableBody {...getTableBodyProps()}>
            {visibleRows.map((row, key) => {
              prepareRow(row);
              return (
                <TableRow
                  key={key}
                  {...row.getRowProps()}
                  sx={{
                    transition: "background-color 160ms ease, box-shadow 160ms ease",
                    ...(row.original?._rowSx || {}),
                  }}
                >
                  {row.cells.map((cell, idx) => (
                    <DataTableBodyCell
                      key={idx}
                      noBorder={noEndBorder && visibleRows.length - 1 === key}
                      align={cell.column.align || "left"}
                      compact={fitContainer}
                      {...cell.getCellProps()}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </MDBox>

      {/* Pagination */}
      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && entriesPerPage === false && totalPageCount === 1 ? 0 : 2}
        gap={2}
        flexWrap="wrap"
        sx={{
          borderTop:
            totalPageCount > 1 || showTotalEntries || entriesPerPage !== false
              ? "1px solid rgba(126, 170, 220, 0.12)"
              : "none",
          background:
            "linear-gradient(180deg, rgba(12,24,39,0.95) 0%, rgba(9,18,31,0.95) 100%)",
        }}
      >
        {(showTotalEntries || entriesPerPage !== false) && (
          <MDBox display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
            {entriesPerPage !== false && (
              <>
                <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                  Rows per page
                </MDTypography>
                <TextField
                  select
                  size="small"
                  value={currentLimit}
                  onChange={handleEntriesPerPageChange}
                  sx={{
                    width: 82,
                    "& .MuiInputBase-root": { height: 38, color: "#ffffff" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.55)",
                    },
                    "& .MuiSvgIcon-root": { color: "#ffffff" },
                  }}
                >
                  {entries.map((entry) => (
                    <MenuItem key={entry} value={entry}>
                      {entry}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
            {showTotalEntries && (
              <MDTypography variant="button" sx={{ color: "#d7e5ff" }} fontWeight="regular">
                Showing {entriesStart}-{entriesEnd} of {totalDocCount}
              </MDTypography>
            )}
          </MDBox>
        )}

        {totalPageCount > 1 && (
          <Pagination
            page={currentPage}
            count={totalPageCount}
            variant="outlined"
            shape="rounded"
            color="primary"
            siblingCount={1}
            boundaryCount={1}
            sx={agriPaginationSx}
            onChange={handlePageChange}
          />
        )}
      </MDBox>
    </TableContainer>
  );
}

// Default props
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [10, 25, 50, 100] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  fitContainer: false,
};

// Typechecking props
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  fitContainer: PropTypes.bool,
  page_no: PropTypes.number,
  hasNextPage: PropTypes.bool,
  hasPrevPage: PropTypes.bool,
  totalPages: PropTypes.number,
  totalDocs: PropTypes.number,
  isnextPage: PropTypes.number,
  prevPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onEntriesPerPageChange: PropTypes.func,
};

export default DataTable;
